import React, { useState, useEffect } from 'react';
import Joyride, { STATUS, ACTIONS } from 'react-joyride';
import { FaSearch, FaFileAlt, FaBuilding, FaBell, FaTimes, FaArrowRight, FaArrowLeft, FaCheck } from 'react-icons/fa';

const TOUR_STORAGE_KEY = 'jobxbharat_tour_completed';

// Logo component for welcome steps
const LogoIcon = () => (
  <img
    src="https://jobx-image.s3.ap-south-1.amazonaws.com/Group+167+(3).svg"
    alt="JobxBharat"
    className="w-5 h-5 object-contain"
  />
);

// Custom Tooltip Component for a professional look
const CustomTooltip = ({
  continuous,
  index,
  step,
  backProps,
  closeProps,
  primaryProps,
  skipProps,
  tooltipProps,
  isLastStep,
  size,
  isDarkMode,
}) => {
  const progress = ((index + 1) / size) * 100;

  return (
    <div
      {...tooltipProps}
      className={`relative overflow-hidden rounded-2xl shadow-2xl ${
        isDarkMode
          ? 'bg-gradient-to-br from-[#1a1a2e] to-[#16213e] border border-gray-700/50'
          : 'bg-gradient-to-br from-white to-gray-50 border border-gray-200/50'
      }`}
      style={{
        maxWidth: '380px',
        minWidth: '320px',
        backdropFilter: 'blur(10px)',
      }}
    >
      {/* Progress bar at top */}
      <div className={`h-1 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
        <div
          className="h-full bg-gradient-to-r from-cyan-500 to-blue-500 transition-all duration-300"
          style={{ width: `${progress}%` }}
        />
      </div>

      {/* Close button */}
      <button
        {...closeProps}
        className={`absolute top-3 right-3 p-1.5 rounded-full transition-colors duration-150 z-10
          ${isDarkMode
            ? 'hover:bg-gray-700/50 text-gray-400 hover:text-gray-200'
            : 'hover:bg-gray-100 text-gray-400 hover:text-gray-600'
          }`}
      >
        <FaTimes className="w-3.5 h-3.5" />
      </button>

      {/* Content */}
      <div className="p-6">
        {/* Icon and Title */}
        {step.icon && (
          <div className="flex items-center gap-3 mb-4">
            <div className={`p-3 rounded-xl ${
              isDarkMode
                ? 'bg-gradient-to-br from-cyan-500/20 to-blue-500/20 text-cyan-400'
                : 'bg-gradient-to-br from-indigo-100 to-purple-100 text-indigo-600'
            }`}>
              {step.icon}
            </div>
            {step.title && (
              <h3 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                {step.title}
              </h3>
            )}
          </div>
        )}

        {/* Description */}
        <p className={`text-sm leading-relaxed ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
          {step.content}
        </p>

        {/* Step indicator */}
        <div className="flex items-center justify-center gap-1.5 mt-5 mb-4">
          {Array.from({ length: size }).map((_, i) => (
            <div
              key={i}
              className={`h-1.5 rounded-full transition-all duration-200 ${
                i === index
                  ? 'w-6 bg-gradient-to-r from-cyan-500 to-blue-500'
                  : i < index
                  ? `w-1.5 ${isDarkMode ? 'bg-cyan-500/50' : 'bg-indigo-400/50'}`
                  : `w-1.5 ${isDarkMode ? 'bg-gray-600' : 'bg-gray-300'}`
              }`}
            />
          ))}
        </div>

        {/* Buttons */}
        <div className="flex items-center justify-between gap-3">
          {/* Skip button */}
          <button
            {...skipProps}
            className={`text-xs font-medium transition-colors duration-150 ${
              isDarkMode ? 'text-gray-500 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'
            }`}
          >
            Skip tour
          </button>

          <div className="flex items-center gap-2">
            {/* Back button */}
            {index > 0 && (
              <button
                {...backProps}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-150 ${
                  isDarkMode
                    ? 'bg-gray-700/50 text-gray-300 hover:bg-gray-700 active:bg-gray-600'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200 active:bg-gray-300'
                }`}
              >
                <FaArrowLeft className="w-3 h-3" />
                Back
              </button>
            )}

            {/* Next/Finish button */}
            <button
              {...primaryProps}
              className={`flex items-center gap-1.5 px-5 py-2 rounded-lg text-sm font-semibold text-white transition-colors duration-150
                bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 active:from-cyan-700 active:to-blue-700
                shadow-lg shadow-cyan-500/25`}
            >
              {isLastStep ? (
                <>
                  <FaCheck className="w-3 h-3" />
                  Got it!
                </>
              ) : (
                <>
                  Next
                  <FaArrowRight className="w-3 h-3" />
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Decorative gradient orb */}
      <div className={`absolute -bottom-10 -right-10 w-32 h-32 rounded-full blur-3xl opacity-20 pointer-events-none ${
        isDarkMode ? 'bg-cyan-500' : 'bg-indigo-500'
      }`} />
    </div>
  );
};

const TechTipsTour = ({ isDarkMode, sidebarOpen, setSidebarOpen, isAuthenticated }) => {
  const [runTour, setRunTour] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const tourCompleted = localStorage.getItem(TOUR_STORAGE_KEY);

    if (!tourCompleted) {
      const timer = setTimeout(() => {
        if (!isMobile && setSidebarOpen) {
          setSidebarOpen(true);
        }
        setRunTour(true);
      }, 1500);

      return () => clearTimeout(timer);
    }
  }, [setSidebarOpen, isMobile]);

  // Desktop steps for authenticated users
  const desktopSteps = [
    {
      target: 'body',
      content: 'Discover thousands of job opportunities across India with our AI-powered platform. Let us give you a quick tour!',
      placement: 'center',
      disableBeacon: true,
      icon: <LogoIcon />,
      title: 'Welcome to JobxBharat!',
    },
    {
      target: '#tour-job-search',
      content: 'Enter your desired job title, preferred location, and target company. Our smart AI will find the best matching opportunities for you.',
      placement: 'top',
      disableBeacon: true,
      icon: <FaSearch className="w-5 h-5" />,
      title: 'Smart Job Search',
    },
    {
      target: '#tour-resume-builder',
      content: 'Create a professional, ATS-friendly resume in minutes. Our AI helps you highlight your skills and experience effectively.',
      placement: 'right',
      disableBeacon: true,
      icon: <FaFileAlt className="w-5 h-5" />,
      title: 'AI Resume Builder',
    },
    {
      target: '#tour-govt-jobs',
      content: 'Access curated government job listings from UPSC, SSC, Railways, Banking, and more. Never miss a sarkari naukri opportunity!',
      placement: 'right',
      disableBeacon: true,
      icon: <FaBuilding className="w-5 h-5" />,
      title: 'Government Jobs',
    },
    {
      target: '#tour-job-alerts',
      content: 'Set up personalized alerts and get notified instantly when jobs matching your criteria are posted. Stay ahead of the competition!',
      placement: 'right',
      disableBeacon: true,
      icon: <FaBell className="w-5 h-5" />,
      title: 'Job Alerts',
    },
  ];

  // Desktop steps for guests (with search limit info)
  const desktopStepsGuest = [
    {
      target: 'body',
      content: 'Discover thousands of job opportunities across India with our AI-powered platform. As a guest, you can try 2 free searches. Sign in to unlock unlimited searches!',
      placement: 'center',
      disableBeacon: true,
      icon: <LogoIcon />,
      title: 'Welcome to JobxBharat!',
    },
    {
      target: '#tour-job-search',
      content: 'Enter your desired job title, preferred location, and target company. Our smart AI will find the best matching opportunities for you.',
      placement: 'top',
      disableBeacon: true,
      icon: <FaSearch className="w-5 h-5" />,
      title: 'Smart Job Search',
    },
    {
      target: '#tour-govt-jobs',
      content: 'Access curated government job listings from UPSC, SSC, Railways, Banking, and more. Never miss a sarkari naukri opportunity!',
      placement: 'right',
      disableBeacon: true,
      icon: <FaBuilding className="w-5 h-5" />,
      title: 'Government Jobs',
    },
    {
      target: 'body',
      content: 'Sign in to unlock unlimited job searches, AI Resume Builder, personalized Job Alerts, search history, and much more!',
      placement: 'center',
      disableBeacon: true,
      icon: <LogoIcon />,
      title: 'Unlock All Features',
    },
  ];

  // Mobile steps (no job search step)
  const mobileStepsAuthenticated = [
    {
      target: 'body',
      content: 'Discover thousands of job opportunities across India with our AI-powered platform. Let us give you a quick tour!',
      placement: 'center',
      disableBeacon: true,
      icon: <LogoIcon />,
      title: 'Welcome to JobxBharat!',
    },
    {
      target: '#mobile-tour-resume',
      content: 'Create a professional, ATS-friendly resume in minutes. Our AI helps you highlight your skills and experience effectively.',
      placement: 'bottom',
      disableBeacon: true,
      icon: <FaFileAlt className="w-5 h-5" />,
      title: 'AI Resume Builder',
    },
    {
      target: '#mobile-tour-govt-jobs',
      content: 'Access curated government job listings from UPSC, SSC, Railways, Banking, and more. Never miss a sarkari naukri opportunity!',
      placement: 'bottom',
      disableBeacon: true,
      icon: <FaBuilding className="w-5 h-5" />,
      title: 'Government Jobs',
    },
    {
      target: '#mobile-tour-job-alerts',
      content: 'Set up personalized alerts and get notified instantly when jobs matching your criteria are posted. Stay ahead!',
      placement: 'bottom',
      disableBeacon: true,
      icon: <FaBell className="w-5 h-5" />,
      title: 'Job Alerts',
    },
  ];

  // Mobile steps for guests (no job search step)
  const mobileStepsGuest = [
    {
      target: 'body',
      content: 'Discover thousands of job opportunities across India with our AI-powered platform. As a guest, you can try 2 free searches. Sign in to unlock unlimited searches!',
      placement: 'center',
      disableBeacon: true,
      icon: <LogoIcon />,
      title: 'Welcome to JobxBharat!',
    },
    {
      target: '#mobile-tour-govt-jobs',
      content: 'Access curated government job listings from UPSC, SSC, Railways, Banking, and more. Never miss a sarkari naukri opportunity!',
      placement: 'bottom',
      disableBeacon: true,
      icon: <FaBuilding className="w-5 h-5" />,
      title: 'Government Jobs',
    },
    {
      target: 'body',
      content: 'Sign in to unlock unlimited job searches, AI Resume Builder, personalized Job Alerts, search history, and much more!',
      placement: 'center',
      disableBeacon: true,
      icon: <LogoIcon />,
      title: 'Unlock All Features',
    },
  ];

  const getSteps = () => {
    if (isMobile) {
      return isAuthenticated ? mobileStepsAuthenticated : mobileStepsGuest;
    }
    return isAuthenticated ? desktopSteps : desktopStepsGuest;
  };

  const handleJoyrideCallback = (data) => {
    const { status, action } = data;
    const finishedStatuses = [STATUS.FINISHED, STATUS.SKIPPED];

    if (finishedStatuses.includes(status)) {
      localStorage.setItem(TOUR_STORAGE_KEY, 'true');
      setRunTour(false);
    }

    // Close on clicking outside (close button)
    if (action === ACTIONS.CLOSE) {
      localStorage.setItem(TOUR_STORAGE_KEY, 'true');
      setRunTour(false);
    }
  };

  const joyrideStyles = {
    options: {
      arrowColor: isDarkMode ? '#1a1a2e' : '#ffffff',
      backgroundColor: 'transparent',
      overlayColor: 'rgba(0, 0, 0, 0.75)',
      primaryColor: isDarkMode ? '#06B6D4' : '#5C6AC4',
      textColor: isDarkMode ? '#f3f4f6' : '#1f2937',
      zIndex: 10000,
    },
    spotlight: {
      borderRadius: 16,
    },
    overlay: {
      backgroundColor: 'rgba(0, 0, 0, 0.75)',
    },
  };

  return (
    <Joyride
      steps={getSteps()}
      run={runTour}
      continuous
      showProgress={false}
      showSkipButton
      callback={handleJoyrideCallback}
      styles={joyrideStyles}
      tooltipComponent={(props) => (
        <CustomTooltip {...props} isDarkMode={isDarkMode} />
      )}
      floaterProps={{
        styles: {
          floater: {
            filter: 'drop-shadow(0 20px 40px rgba(0, 0, 0, 0.3))',
          },
        },
      }}
      disableScrolling={isMobile}
      spotlightPadding={8}
    />
  );
};

export default TechTipsTour;
